import { FC, useEffect, useRef } from 'react';
import { t } from 'i18next';
import {
  IXsollaWidgetPaymentInfo,
  openXsollaWidgetSession,
  TXsollaNormalizedWidgetEvent
} from './xsollaWidgetAdapter';

type TXsollaCheckoutProps = {
  token: string;
  onSuccess: (paymentInfo: IXsollaWidgetPaymentInfo) => void;
  onError: (error: string) => void;
  onClose: () => void;
  onWidgetOpen?: () => void;
  onWidgetStage?: (event: TXsollaNormalizedWidgetEvent) => void;
  onPaymentInfo?: (paymentInfo?: IXsollaWidgetPaymentInfo, event?: TXsollaNormalizedWidgetEvent) => void;
  onPending?: (paymentInfo: IXsollaWidgetPaymentInfo) => void;
  sandbox?: boolean;
};

export const XsollaCheckout: FC<TXsollaCheckoutProps> = ({
  token,
  onSuccess,
  onError,
  onClose,
  onWidgetOpen,
  onWidgetStage,
  onPaymentInfo,
  onPending,
  sandbox = false
}) => {
  const isInitialized = useRef(false);
  const resolutionState = useRef<'success' | 'error' | null>(null);

  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const onCloseRef = useRef(onClose);
  const onWidgetOpenRef = useRef(onWidgetOpen);
  const onWidgetStageRef = useRef(onWidgetStage);
  const onPaymentInfoRef = useRef(onPaymentInfo);
  const onPendingRef = useRef(onPending);
  onSuccessRef.current = onSuccess;
  onErrorRef.current = onError;
  onCloseRef.current = onClose;
  onWidgetOpenRef.current = onWidgetOpen;
  onWidgetStageRef.current = onWidgetStage;
  onPaymentInfoRef.current = onPaymentInfo;
  onPendingRef.current = onPending;

  useEffect(() => {
    if (isInitialized.current) {
      return;
    }
    isInitialized.current = true;

    let cleanup = () => {};

    const handleEvent = (event: TXsollaNormalizedWidgetEvent) => {
      switch (event.type) {
        case 'open':
          onWidgetOpenRef.current?.();
          onWidgetStageRef.current?.(event);
          return;
        case 'stage':
          onWidgetStageRef.current?.(event);
          return;
        case 'payment_info':
          onPaymentInfoRef.current?.(event.paymentInfo, event);
          onWidgetStageRef.current?.(event);
          return;
        case 'success':
          resolutionState.current = 'success';
          onWidgetStageRef.current?.(event);
          onSuccessRef.current(event.paymentInfo);
          return;
        case 'pending':
          onWidgetStageRef.current?.(event);
          onPendingRef.current?.(event.paymentInfo);
          return;
        case 'error':
          onWidgetStageRef.current?.(event);
          if (!resolutionState.current) {
            resolutionState.current = 'error';
            onErrorRef.current(t('xsolla:payment-status-unconfirmed'));
          }
          return;
        case 'close':
          onWidgetStageRef.current?.(event);
          if (!resolutionState.current) {
            onCloseRef.current();
          }
      }
    };

    const initWidget = async () => {
      try {
        resolutionState.current = null;
        cleanup = await openXsollaWidgetSession({
          token,
          sandbox,
          onEvent: handleEvent
        });
      } catch (error: unknown) {
        console.error('Failed to initialize Xsolla Pay Station:', error);
        onErrorRef.current(t('xsolla:checkout-widget-init-failed'));
      }
    };

    void initWidget();

    return () => {
      cleanup();
    };
  }, [sandbox, token]);

  return null;
};

export default XsollaCheckout;
